import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { ApiCallingV2Service, API_CONFIG, DropdownData, AnalyticsEvent, AnalyticsService } from '@zayan-erp/shared';
import { DsmDealAddendumOption } from '../models/dsm-deposit.models';
import {
  DsmAddendumOptionsPayload,
  DsmDealOption,
  DsmDealOptionProduct,
  DsmGenerateAddendumPdfRequest,
  DsmPrintAddendumOption,
  DsmUpdateDealAddendumPayload
} from '../models/dsm-deal-options-view.models';
import { DsmAddendumLog, DsmExcludedBuilderReference } from '../models/dsm-deal-options.models';
import { DsmRoomReplacementRerun } from '../models/dsm-room-replacement-review.models';
import { DsmApsDealContextService } from './dsm-aps-deal-context.service';

@Injectable({ providedIn: 'root' })
export class DsmDealOptionsService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #baseUrl = this.#apiConfig.baseUrlV2;
  #analytics = inject(AnalyticsService);
  #dealContext = inject(DsmApsDealContextService);

  getDealAddendumsForDropdown(
    dealId: string,
    excludeCancelled: boolean = true,
    excludeLocked: boolean = false
  ): Observable<DropdownData[]> {
    return this.#http
      .getData<DsmDealAddendumOption[]>(
        `${this.#baseUrl}/deals/${dealId}/addendums?excludeCancelled=${excludeCancelled}`,
        {
          showLoader: true,
          showMessage: false,
          showError: false
        }
      )
      .pipe(
        map((res: DsmDealAddendumOption[] | null | undefined) =>
          (res ?? [])
            .filter(item => !(excludeLocked && item.isLocked))
            .map(
              (item: DsmDealAddendumOption): DropdownData => ({
                id: item.id,
                firstLabel: item.builderReference.reference,
                secondLabel: `${item.addendumType.name}`,

                data: { hasValidOptions: (item.products ?? []).some(product => !product.isCancelled) }
              })
            )
        )
      );
  }

  /** @deprecated Use getDealAddendumsForDropdown */
  getDealOptions(dealId: string): Observable<DropdownData[]> {
    return this.getDealAddendumsForDropdown(dealId);
  }

  getDealAddendums(dealId: string): Observable<DsmDealOption[]> {
    return this.#http.getData<DsmDealOption[]>(`${this.#baseUrl}/deals/${dealId}/addendums`, {
      showLoader: true,
      showMessage: false,
      showError: false
    });
  }

  getAddendums(lotId: string, dealId: string, showLoader: boolean = true): Observable<DsmDealOption[]> {
    const params = `lotId=${lotId}&dealId=${dealId}`;
    return this.#http.getData<DsmDealOption[]>(`${this.#baseUrl}/addendums?${params}`, {
      showLoader,
      showMessage: false
    });
  }

  getPrintAddendumOptions(): Observable<DsmPrintAddendumOption[]> {
    return this.#http.getData<DsmPrintAddendumOption[]>(`${this.#baseUrl}/lookup/print-addendum-options`, {
      showLoader: true,
      showMessage: false
    });
  }

  getDealOptionLogs(optionId: string): Observable<DsmAddendumLog[]> {
    return this.#http.getData<DsmAddendumLog[]>(`${this.#baseUrl}/addendums/${optionId}/logs`, {
      showLoader: true,
      showMessage: false
    });
  }

  deleteOption(optionId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.#baseUrl}/addendums/${optionId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Addendum deleted'
    });
  }

  deleteAddendumProduct(productId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.#baseUrl}/addendum-products/${productId}`, {
      showLoader: true,
      showMessage: true,
      message: 'Product deleted'
    });
  }

  updateAddendum(
    addendumId: string,
    payload: Partial<DsmUpdateDealAddendumPayload>,
    showLoader: boolean = true
  ): Observable<void> {
    return this.#http.putData<void>(`${this.#baseUrl}/addendums/${addendumId}`, payload, {
      showLoader,
      showMessage: true,
      message: 'Addendum updated'
    });
  }

  updateBuilderReference(addendumId: string, builderReferenceId: string): Observable<void> {
    return this.#http.patchData<void>(
      `${this.#baseUrl}/addendums/${addendumId}/builder-reference`,
      { builderReferenceId },
      {
        showLoader: true,
        showMessage: true,
        message: 'Builder reference updated'
      }
    );
  }

  toggleApproval(
    addendumId: string,
    payload: { toggleBuyerApproved?: boolean; toggleBuilderApproved?: boolean },
    showLoader: boolean = true
  ): Observable<void> {
    return this.#http
      .patchData<void>(`${this.#baseUrl}/addendums/${addendumId}/toggle-approval`, payload, {
        showLoader,
        showMessage: true,
        message: 'Approval updated'
      })
      .pipe(
        tap(() => {
          const ctx = this.#dealContext.currentDealCtx();
          if (!ctx) return;
          const isBuyer = payload.toggleBuyerApproved !== undefined;
          this.#analytics.capture(AnalyticsEvent.AddendumApprovalToggled, {
            ...ctx,
            addendum_id: addendumId,
            approval_type: isBuyer ? 'buyer' : 'builder',
            approved: isBuyer ? !!payload.toggleBuyerApproved : !!payload.toggleBuilderApproved
          });
        })
      );
  }

  addAddendumOptions(addendumId: string, payload: DsmAddendumOptionsPayload[]): Observable<void> {
    return this.#http.postData<void>(
      `${this.#baseUrl}/addendums/${addendumId}/products`,
      { showLoader: true, showMessage: false, message: 'Options added' },
      payload
    );
  }

  getAddendumOptionDetailsById(addendumProductId: string): Observable<DsmDealOptionProduct> {
    return this.#http.getData<DsmDealOptionProduct>(`${this.#baseUrl}/addendum-products/${addendumProductId}`, {
      showLoader: false,
      showMessage: false
    });
  }

  updateAddendumOption(
    addendumOptionId: string,
    payload: DsmAddendumOptionsPayload,
    showLoader: boolean = true
  ): Observable<void> {
    return this.#http.putData<void>(`${this.#baseUrl}/addendum-products/${addendumOptionId}`, payload, {
      showLoader,
      showMessage: true,
      message: 'Option updated'
    });
  }

  /** Moves an option on a locked addendum: only its location changes, never its price or quantity. */
  /** A null locationId takes the room off the line; the option itself stays. */
  updateAddendumOptionLocation(addendumOptionId: string, locationId: string | null): Observable<void> {
    return this.#http.putData<void>(
      `${this.#baseUrl}/addendum-products/${addendumOptionId}/location`,
      { locationId },
      {
        showLoader: true,
        showMessage: true,
        message: 'Location updated'
      }
    );
  }

  updateIncentive(
    addendumOptionId: string,
    payload: { incentiveAmount: number },
    showLoader: boolean = true
  ): Observable<void> {
    return this.#http.putData<void>(
      `${this.#baseUrl}/addendum-products/${addendumOptionId}/incentive-amount`,
      payload,
      {
        showLoader,
        showMessage: true,
        message: 'Incentive updated'
      }
    );
  }

  updateDiscount(
    addendumOptionId: string,
    payload: { discountTypeId: string; discountValue: number },
    showLoader: boolean = true
  ): Observable<void> {
    return this.#http.putData<void>(`${this.#baseUrl}/addendum-products/${addendumOptionId}/discount`, payload, {
      showLoader,
      showMessage: true,
      message: 'Discount updated'
    });
  }

  applyDiscountToAll(addendumId: string, percentage: number, showLoader: boolean = true): Observable<void> {
    return this.#http.postData<void>(
      `${this.#baseUrl}/addendum-products/addendum/${addendumId}/apply-discount`,
      { showLoader, showMessage: true, message: 'Discount applied to all options' },
      { percentage }
    );
  }

  clearAllDiscounts(addendumId: string, showLoader: boolean = true): Observable<void> {
    return this.#http.postData<void>(`${this.#baseUrl}/addendum-products/addendum/${addendumId}/clear-discounts`, {
      showLoader,
      showMessage: true,
      message: 'Discounts cleared'
    });
  }

  updateContractFlags(
    dealId: string,
    payload: { eligibleForGSTRebate: boolean; investmentProperty: boolean }
  ): Observable<void> {
    return this.#http.patchData<void>(`${this.#baseUrl}/deals/${dealId}/contract/flags`, payload, {
      showLoader: true,
      showMessage: true,
      message: 'Contract flags updated'
    });
  }

  getLotAddendums(lotId: string, showLoader: boolean = true): Observable<DsmDealOption[]> {
    return this.#http.getData<DsmDealOption[]>(`${this.#baseUrl}/lot/${lotId}/addendums`, {
      showLoader,
      showMessage: false
    });
  }

  cancelAddendum(addendumId: string): Observable<void> {
    return this.#http.postData<void>(`${this.#baseUrl}/addendums/${addendumId}/cancel`, {
      showLoader: true,
      showMessage: true,
      message: 'Addendum cancelled'
    });
  }

  previewRoomReplacementRerun(addendumId: string): Observable<DsmRoomReplacementRerun> {
    return this.#http.getData<DsmRoomReplacementRerun>(
      `${this.#baseUrl}/addendums/${addendumId}/rerun-room-replacement-rules/preview`,
      { showLoader: false, showMessage: false, showError: false }
    );
  }

  rerunRoomReplacementRules(
    addendumId: string,
    notifyConsultants: boolean,
    showError = true
  ): Observable<DsmRoomReplacementRerun> {
    return this.#http.postData<DsmRoomReplacementRerun>(
      `${this.#baseUrl}/addendums/${addendumId}/rerun-room-replacement-rules`,
      { showLoader: false, showMessage: false, showError },
      { notifyConsultants }
    );
  }

  cancelAddendumProduct(addendumProductId: string): Observable<void> {
    return this.#http.postData<void>(`${this.#baseUrl}/addendum-products/${addendumProductId}/cancel`, {
      showLoader: true,
      showMessage: true,
      message: 'Addendum product cancelled'
    });
  }

  downloadAddendumsPdf(
    dealId: string,
    addendumIds: string[],
    selectedOptionIds: number[],
    downloadFileName?: string,
    forSigningPreview = false
  ): Observable<void> {
    const body: DsmGenerateAddendumPdfRequest = {
      addendumIds,
      selectedOptionIds,
      forSigningPreview
    };
    return this.#http.downloadReportFileWithBody(
      `${this.#baseUrl}/deals/${dealId}/addendums/dsm-pdf`,
      body,
      { showLoader: true, showMessage: true },
      downloadFileName
    );
  }

  excludeFromSigningPreview(): Observable<DsmExcludedBuilderReference[]> {
    return this.#http
      .getData<DsmExcludedBuilderReference[]>(`${this.#baseUrl}/BuilderReference/excluded`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(res => res ?? []));
  }
}
